// Sea ice for a date: the month's concentration per hex, and the sources that back a month.
//
//   seaice.bin month m ──▶ Uint8Array over the whole grid (0 off the listed hexes)
//   date ─▶ month index: before the first month = no data, after the last = the last month

import { HEX_COUNT } from '../core/grid.ts';
import type { DateStr, Source, SourceId } from '../core/schema.ts';
import type { SeaIce } from '../data/index.ts';

const MONTHS_PER_YEAR = 12;
const YEAR_CHARS = 4;
const MONTH_FROM = 5;
const MONTH_TO = 7;
/** The bake's ids: ice:nsidc-{monthly|daily}-{n|s}-{YYYYMM|YYYYMMDD} (tools/bake/worldciv_bake/seaice_sources.py). */
const NSIDC_PREFIX = 'ice:nsidc-';
/** Registry ids that cover only one hemisphere carry one of these tokens ('ice:nsidc-monthly-n-200001'). */
const NORTH_TOKENS = ['n', 'north'];
const SOUTH_TOKENS = ['s', 'south'];

export type Hemisphere = 'north' | 'south';

function monthNumber(tag: string): number {
  return Number(tag.slice(0, YEAR_CHARS)) * MONTHS_PER_YEAR + Number(tag.slice(MONTH_FROM, MONTH_TO)) - 1;
}

/** Block index that serves `date`, or null before the first month. */
export function iceMonth(ice: SeaIce, date: DateStr): number | null {
  const m = monthNumber(date) - monthNumber(ice.first);
  if (m < 0) {
    return null;
  }
  return Math.min(m, ice.months - 1);
}

/** 'YYYY-MM' of block `m`. */
export function iceMonthTag(ice: SeaIce, m: number): string {
  const n = monthNumber(ice.first) + m;
  const year = String(Math.floor(n / MONTHS_PER_YEAR)).padStart(YEAR_CHARS, '0');
  const month = String((n % MONTHS_PER_YEAR) + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/** Concentration of block `m` over the whole grid (new array; the world caches and shares it). */
export function iceArray(ice: SeaIce, m: number): Uint8Array {
  const out = new Uint8Array(HEX_COUNT);
  const base = m * ice.hexes.length;
  for (let i = 0; i < ice.hexes.length; i++) {
    out[ice.hexes[i]] = ice.conc[base + i];
  }
  return out;
}

/**
 * The registry sources of one month's ice files for a hemisphere: NSIDC ids that carry the month
 * as YYYYMM or YYYY-MM. An id naming the other hemisphere is left out; one naming neither is kept.
 */
export function iceSources(sources: Map<SourceId, Source>, tag: string, side: Hemisphere): SourceId[] {
  const compact = tag.replace('-', '');
  const other = side === 'north' ? SOUTH_TOKENS : NORTH_TOKENS;
  const out: SourceId[] = [];
  for (const id of sources.keys()) {
    if (!id.startsWith(NSIDC_PREFIX) || !(id.includes(compact) || id.includes(tag))) {
      continue;
    }
    if (id.slice(NSIDC_PREFIX.length).split(/[^a-z0-9]+/).some((t) => other.includes(t))) {
      continue;
    }
    out.push(id);
  }
  return out;
}
